/**
 * Колокольчик и панель уведомлений в шапке — Блок 2 · Ф7 · В11-a
 * (**Р-132, «Уведомления в вебе — только колокольчик, панель и пометка»**).
 *
 * ─── ЧТО ПОКАЗЫВАЕТ ПАНЕЛЬ ────────────────────────────────────────────────
 *
 * Очередь уведомлений смотрящего за 30 дней — ВСЮ, независимо от того, дошла
 * ли строка в мессенджер. В этом смысл панели: сотрудник без привязанного
 * бота узнаёт о назначении здесь, потому что больше негде. Строк о
 * собственных действиях в ней нет — их нет в очереди (Р-64).
 *
 * ─── СЧЁТЧИК И «ПРОЧИТАНО» ────────────────────────────────────────────────
 *
 * Серверного признака прочтения нет. Память «видел» лежит в браузере
 * (`lib/notification-seen.ts`): метка — `asOf` ОТВЕТА СЕРВЕРА — и
 * идентификаторы показанных строк моложе метки.
 *
 *   - метки нет (первый заход после выката, чужой браузер) — метка ставится
 *     первым же ответом, счётчик НОЛЬ: накопленное за 30 дней не зажигает
 *     колокольчик;
 *   - метка двигается ТОЛЬКО при открытой панели — ответ опроса при закрытой
 *     её не трогает, иначе счётчик гас бы сам;
 *   - панель открыта — всё показанное видено; строки, бывшие новыми на момент
 *     открытия, остаются выделенными, пока панель не закроют;
 *   - счёт идёт по первой странице; если она вся новая и есть следующая —
 *     «N+».
 *
 * ─── «СКРЫТЬ ПРОЧИТАННЫЕ» (В11-e) ─────────────────────────────────────────
 *
 * Решение владельца 08.10.2026, вариант Б: кнопка убирает из списка строки,
 * прочитанные НА МИГ НАЖАТИЯ; скрытие живёт в браузере (на другом устройстве
 * строки остаются) и снимается целиком ссылкой «Показать скрытые».
 *
 *   - скрывается ТОЛЬКО то, что в миг нажатия ЗАГРУЖЕНО в панель и стоит в
 *     ней без отметки «новое». Запоминаются идентификаторы этих строк, а не
 *     граница по времени: «всё не новее метки» прятало бы и то, чего человек
 *     не видел ни разу (разбор — `lib/notification-seen.ts`);
 *   - это разовое действие, а не постоянный отбор: строка, пришедшая после
 *     нажатия, видна и новой, и прочитанной — до следующего нажатия;
 *   - НОВАЯ СТРОКА НЕ СКРЫВАЕТСЯ НИКОГДА, что бы ни лежало в хранилище:
 *     счётчик на колокольчике и список не расходятся. Сам счётчик о скрытии
 *     не знает;
 *   - всё глубже загруженного достижимо: «Показать ещё» скрытием НЕ гасится.
 *     Чтобы нажатие не приносило пустоту, страница целиком из скрытых строк
 *     пролистывается тем же нажатием — не дальше `MORE_HOPS_MAX` страниц;
 *   - «новых нет» панель говорит только при исчерпанной ленте и удавшемся
 *     обновлении.
 *
 * При открытии фокус встаёт на саму панель, а не на первый элемент в ней:
 * первым стала эта кнопка, и «Enter, Enter» с клавиатуры молча скрывал бы
 * прочитанное.
 *
 * ─── ЗАПРОСЫ ──────────────────────────────────────────────────────────────
 *
 * Тактом опрашивается только первая страница (`useNotificationFeedHead`,
 * разбор бюджета — в `api/notification-feed.ts`). Страницы «Показать ещё»
 * живут в состоянии панели, тактом не перезапрашиваются и сбрасываются
 * закрытием панели.
 *
 * ─── ИСХОД В МЕССЕНДЖЕРЕ — НЕ ПОКАЗЫВАЕТСЯ ───────────────────────────────
 *
 * Подписей «дошло / не дошло» под строками НЕТ — решение владельца 04.10.2026
 * на вопрос «нужны ли под строками подписи о том, дошло ли уведомление в
 * мессенджер»: «Б — без подписей». Цена названа и принята: человек по панели
 * не видит, что в мессенджер ему ничего не пришло. Единственное, что панель
 * говорит о мессенджере, — плашка «бот не привязан» в шапке, по живому
 * состоянию привязки смотрящего из профиля.
 *
 * Поле `messenger` в ответе ленты при этом приходит и здесь не читается —
 * оно оставлено под отложенный отчёт о доставке (докблок поля в
 * `@aris/shared`).
 *
 * ─── ОТКАЗЫ ───────────────────────────────────────────────────────────────
 *
 * Отказ опроса тоста не показывает: при закрытой панели о нём говорит отметка
 * на колокольчике и его имя для скринридера, при открытой — строка с
 * «Повторить». Бросок в самом колокольчике гасит только колокольчик: он стоит
 * в шапке каждого экрана, и без своей границы ошибок ронял бы экран целиком.
 * На его месте остаётся неактивный значок с подсказкой — молча исчезнувший
 * колокольчик выглядел бы как «уведомлений нет»; переход на другой экран
 * пробует нарисовать его заново.
 *
 * Супервайзеру колокольчик не рисуется: маршрут отвечает ему отказом.
 */
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, BellOff, ChevronRight, TriangleAlert } from 'lucide-react';

import {
  countUnseenNotifications,
  isNotificationUnseen,
  type NotificationFeedItem,
  type NotificationFeedResponse,
} from '@aris/shared';

import { fetchNotificationFeedPage, useNotificationFeedHead } from '@/api/notification-feed';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useMe } from '@/hooks/use-me';
import { notificationHeadline, notificationsRu } from '@/i18n';
import { formatDate, formatIsoDateRu } from '@/lib/date';
import {
  clearHidden,
  hiddenReadStorageKey,
  hideShownRead,
  parseSeenIds,
  readHidden,
  readSeenAsOf,
  readSeenIds,
  seenAsOfStorageKey,
  seenIdsStorageKey,
  writeSeen,
  type HiddenEntry,
} from '@/lib/notification-seen';
import { cn } from '@/lib/utils';

import { notificationTarget, type ViewerLevel } from './notification-target';

const VIEWER_LEVELS: readonly ViewerLevel[] = ['EMPLOYEE', 'MANAGER', 'ADMIN', 'SUPERVISOR'];
const EMPTY_IDS: ReadonlySet<string> = new Set();
const NO_HIDDEN: readonly HiddenEntry[] = [];
/**
 * Сколько страниц подряд одно нажатие «Показать ещё» пролистывает, если все
 * строки на них скрыты. Потолок — от бюджета запросов (`api/notification-feed.ts`).
 */
const MORE_HOPS_MAX = 5;

/** Зона касания не меньше 44px на телефоне; на широком экране — по вёрстке. */
const TOUCH_TARGET = 'min-h-11 md:min-h-0';

function viewerLevel(accessLevel: unknown): ViewerLevel | null {
  return VIEWER_LEVELS.find((level) => level === accessLevel) ?? null;
}

interface RowProps {
  item: NotificationFeedItem;
  unseen: boolean;
  level: ViewerLevel | null;
  onNavigate: () => void;
}

function NotificationRow({ item, unseen, level, onNavigate }: RowProps): React.ReactElement {
  const target = notificationTarget(item, level);
  const { title, sectionName, projectName, dueDate, authorName } = item.display;
  // Пустое название — законное состояние: тогда работу называет раздел.
  const workLabel = title ?? sectionName ?? notificationsRu.untitled;

  const body = (
    <>
      <span className="flex items-baseline justify-between gap-2">
        <span className={cn('text-sm text-ink', unseen && 'font-semibold')}>
          {notificationHeadline(item)}
          {unseen ? (
            <span className="ml-1.5 rounded-sm bg-accent-wash px-1 py-0.5 text-[10px] font-medium uppercase text-accent-ink">
              {notificationsRu.newMark}
            </span>
          ) : null}
        </span>
        <time dateTime={item.occurredAt} className="shrink-0 text-[11px] text-muted">
          {formatDate(item.occurredAt, 'd MMM, HH:mm')}
        </time>
      </span>
      {/* Работа и объект — РАЗНЫМИ строками: в одной длинное название работы
          съедало название объекта целиком. Полный текст — в подсказке. */}
      <span className="block truncate text-xs text-ink" title={workLabel}>
        {workLabel}
      </span>
      {projectName === null ? null : (
        <span
          data-testid="notification-project"
          className="block truncate text-xs text-muted"
          title={projectName}
        >
          {projectName}
        </span>
      )}
      <span className="block text-[11px] text-muted">
        {notificationsRu.due} {formatIsoDateRu(dueDate)}
        {authorName === null ? '' : ` · ${authorName}`}
      </span>
    </>
  );

  return (
    <li
      data-testid="notification-row"
      data-kind={item.kind}
      data-unseen={unseen ? 'true' : 'false'}
      data-link={target === null ? 'false' : 'true'}
    >
      {target === null ? (
        <div className="block px-2 py-1.5 pr-7 text-left">{body}</div>
      ) : (
        // Строка-ссылка отличима до наведения: у неё стрелка справа.
        <Link
          to={target}
          onClick={onNavigate}
          className="relative block rounded-sm px-2 py-1.5 pr-7 text-left hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
          {body}
          <ChevronRight
            aria-hidden
            className="absolute right-1.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
          />
        </Link>
      )}
    </li>
  );
}

/** Заглушка загрузки — три строки той же высоты, что настоящие. */
function LoadingRows(): React.ReactElement {
  return (
    <div data-testid="notification-loading" className="space-y-2 px-2 py-2">
      <span className="sr-only">{notificationsRu.loading}</span>
      {[0, 1, 2].map((n) => (
        <Skeleton key={n} className="h-12 w-full" />
      ))}
    </div>
  );
}

type MoreState = 'idle' | 'loading' | 'error';

function NotificationBellInner(): React.ReactElement | null {
  const { data: me } = useMe();
  const level = viewerLevel(me?.accessLevel);
  const userId = me?.id;
  const allowed = level !== null && level !== 'SUPERVISOR';

  const head = useNotificationFeedHead(userId, allowed);
  const headPage = head.data;
  const titleId = React.useId();

  const [open, setOpen] = React.useState(false);
  const [seenAsOf, setSeenAsOf] = React.useState<string | null>(null);
  const [seenIds, setSeenIds] = React.useState<ReadonlySet<string>>(EMPTY_IDS);
  /** Память «видел» на момент открытия — по ней выделяются «новые», пока открыто. */
  const [highlight, setHighlight] = React.useState<{
    asOf: string | null;
    ids: ReadonlySet<string>;
  } | null>(null);
  /**
   * Строки первой страницы, которые человек УЖЕ видел в этом открытии панели.
   *
   * Первая страница перезапрашивается (тактом, по открытию), и новые строки
   * сдвигают её окно: строка, бывшая на ней последней, со страницы уходит. Без
   * этой памяти она выпадала из открытой панели на глазах, если следующая
   * страница уже догружена и начинается дальше (приёмка da, Н-5).
   */
  const [headSeen, setHeadSeen] = React.useState<NotificationFeedItem[]>([]);
  /** Страницы, догруженные «Показать ещё». Тактом не перезапрашиваются. */
  const [extraPages, setExtraPages] = React.useState<NotificationFeedResponse[]>([]);
  const [moreState, setMoreState] = React.useState<MoreState>('idle');
  /** Скрытые прочитанные — из хранилища браузера, своё у каждого пользователя. */
  const [hidden, setHidden] = React.useState<readonly HiddenEntry[]>(NO_HIDDEN);
  const hiddenSet = React.useMemo(() => new Set(hidden.map(([id]) => id)), [hidden]);
  /** Объявление для скринридера: список изменился, фокус остался на месте. */
  const [announce, setAnnounce] = React.useState('');
  const panel = React.useRef<HTMLDivElement>(null);
  const isVisibleNow = React.useRef<(item: NotificationFeedItem) => boolean>(() => true);
  const hideButton = React.useRef<HTMLButtonElement>(null);
  /** Номер «сеанса» панели: ответ догрузки, пришедший после закрытия, отбрасывается. */
  const session = React.useRef(0);

  // Колокольчик размонтирован (выход, смена экрана оболочки) — догрузка,
  // начатая в панели, дальше не идёт: её ответ отбрасывается, как при закрытии.
  React.useEffect(
    () => () => {
      session.current += 1;
    },
    [],
  );

  // Память читается при появлении пользователя (и при его смене в той же вкладке).
  React.useEffect(() => {
    setSeenAsOf(userId === undefined ? null : readSeenAsOf(userId));
    setSeenIds(userId === undefined ? EMPTY_IDS : readSeenIds(userId));
    setHidden(userId === undefined ? NO_HIDDEN : readHidden(userId));
  }, [userId]);

  // Соседняя вкладка того же браузера открыла панель — счётчик гаснет и здесь.
  React.useEffect(() => {
    if (userId === undefined) return undefined;
    const onStorage = (event: StorageEvent): void => {
      if (event.key === seenAsOfStorageKey(userId)) setSeenAsOf(event.newValue);
      if (event.key === seenIdsStorageKey(userId)) setSeenIds(parseSeenIds(event.newValue));
      // Событие — только повод ПЕРЕЧИТАТЬ хранилище: его `newValue` бывает
      // старше того, что там уже лежит (эта вкладка успела записать поверх).
      // `key === null` — соседняя вкладка очистила хранилище целиком.
      if (event.key === null || event.key === hiddenReadStorageKey(userId)) {
        setHidden(readHidden(userId));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);

  React.useEffect(() => {
    if (userId === undefined || headPage === undefined) return;
    const firstVisit = readSeenAsOf(userId) === null;
    // Метка двигается в двух случаях, и только в них: её ещё нет (первый ответ,
    // счётчик ноль) либо панель открыта (показанное — видено). Ответ опроса
    // при закрытой панели метку НЕ трогает — иначе счётчик гас бы сам.
    if (!firstVisit && !open) return;
    // Показанные строки моложе новой метки запоминаются поимённо: метка отстаёт
    // от выборки на запас, а момент строки бывает и позже часов базы.
    const shown = open
      ? headPage.items
          .filter((item) => isNotificationUnseen(item, headPage.asOf))
          .map((item) => item.id)
      : [];
    writeSeen(userId, headPage.asOf, shown);
    setSeenAsOf(headPage.asOf);
    setSeenIds(new Set(shown));
  }, [userId, headPage, open]);

  React.useEffect(() => {
    if (!open || headPage === undefined) return;
    setHeadSeen((seen) => {
      const fresh = new Set(headPage.items.map((item) => item.id));
      return [...headPage.items, ...seen.filter((item) => !fresh.has(item.id))];
    });
  }, [open, headPage]);

  if (!allowed || userId === undefined) return null;

  const unseen = headPage
    ? countUnseenNotifications(headPage, seenAsOf, seenIds)
    : { count: 0, more: false };
  const badge = unseen.count === 0 ? null : `${unseen.count}${unseen.more ? '+' : ''}`;
  /** Последнее обновление не удалось — видно и при закрытой панели, без тоста. */
  const stale = head.isError;

  const handleOpenChange = (next: boolean): void => {
    // Память «до открытия» запоминается ДО сдвига — иначе выделять было бы нечего.
    setHighlight(next ? { asOf: seenAsOf, ids: seenIds } : null);
    if (!next) {
      session.current += 1;
      setExtraPages([]);
      setHeadSeen([]);
      setMoreState('idle');
      setAnnounce('');
    } else if (head.isStale && !head.isFetching) {
      // Человек открыл панель — показать свежее, а не снимок трёхминутной
      // давности. Запрос уходит, только если ответ старше порога свежести
      // (тот же, что у возврата фокуса), то есть не чаще раза в минуту.
      void head.refetch();
    }
    setOpen(next);
  };

  // Всё показанное в этом открытии: живая первая страница, её прежние строки
  // и догруженные страницы. Повторы по `id` снимаются (первая страница могла
  // сдвинуться и наехать на догруженную), порядок — тот же, что у сервера:
  // от новых к старым, при равном моменте — по убыванию `id`.
  const byId = new Map<string, NotificationFeedItem>();
  for (const item of [
    ...(headPage?.items ?? []),
    ...headSeen,
    ...extraPages.flatMap((page) => page.items),
  ]) {
    if (!byId.has(item.id)) byId.set(item.id, item);
  }
  const items = [...byId.values()].sort((a, b) => {
    const byMoment = Date.parse(b.occurredAt) - Date.parse(a.occurredAt);
    if (byMoment !== 0) return byMoment;
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
  // Курсор продолжения — у ПОСЛЕДНЕЙ догруженной страницы; пока догрузок не
  // было — у первой. Сдвиг первой страницы его не трогает: продолжение идёт
  // оттуда, где человек остановился.
  const lastPage = extraPages[extraPages.length - 1] ?? headPage;

  const nextCursor = lastPage?.nextCursor ?? null;

  const isNew = (item: NotificationFeedItem): boolean =>
    highlight !== null && isNotificationUnseen(item, highlight.asOf, highlight.ids);
  // Новая строка видна всегда — иначе счётчик обещал бы то, чего в списке нет.
  const isVisible = (item: NotificationFeedItem): boolean => isNew(item) || !hiddenSet.has(item.id);
  // Догрузка идёт в несколько запросов и между ними скрытие может измениться
  // («Показать скрытые», соседняя вкладка) — её шаги читают правило отсюда.
  isVisibleNow.current = isVisible;
  const visible = items.filter(isVisible);
  const hiddenCount = items.length - visible.length;
  const canHide = visible.some((item) => !isNew(item));

  // Одинаковый текст скринридер второй раз не объявляет — два объявления
  // подряд обязаны различаться, поэтому у каждого есть вторая формулировка.
  const say = (text: string, again: string): void => {
    setAnnounce((previous) => (previous === text ? again : text));
  };

  const hideRead = (): void => {
    // `aria-disabled`, как у «Показать ещё»: кнопка не теряет фокус.
    if (!canHide || headPage === undefined) return;
    // Только ЗАГРУЖЕННЫЕ строки без отметки «новое».
    const shownRead = items.filter((item) => !isNew(item));
    const next = hideShownRead(userId, shownRead, headPage.asOf);
    setHidden(next);
    // Число — по множеству, которое действительно записано: строка, не
    // попавшая в него (момент дальше суток впереди сервера), осталась видна.
    const nowHidden = new Set(next.map(([id]) => id));
    const count = String(shownRead.filter((item) => nowHidden.has(item.id)).length);
    say(
      notificationsRu.hiddenAnnounce.replace('{N}', count),
      notificationsRu.hiddenAnnounceAgain.replace('{N}', count),
    );
  };

  const showHidden = (): void => {
    clearHidden(userId);
    setHidden(NO_HIDDEN);
    say(notificationsRu.shownAnnounce, notificationsRu.shownAnnounceAgain);
    // Ссылка сейчас исчезнет — фокус остаётся в панели, а не уходит на <body>.
    hideButton.current?.focus();
  };

  const loadMore = (): void => {
    // `aria-disabled`, а не `disabled`: выключенная кнопка теряет фокус, и он
    // уходит из панели на `<body>`. Повторное нажатие гасится здесь.
    if (nextCursor === null || moreState === 'loading') return;
    const startedIn = session.current;
    setMoreState('loading');
    const step = (cursor: string, hopsLeft: number): void => {
      fetchNotificationFeedPage(cursor).then(
        (page) => {
          if (session.current !== startedIn) return;
          setExtraPages((pages) => [...pages, page]);
          // Страница целиком из скрытых строк на экране ничего не изменила бы:
          // нажатие выглядело бы несработавшим. Листаем дальше тем же нажатием.
          // Правило видимости — ТЕКУЩЕЕ, а не на момент нажатия: скрытие могли
          // снять посреди цепочки, и тогда страница уже видима — листать незачем.
          const shows = page.items.some((item) => isVisibleNow.current(item));
          if (!shows && page.nextCursor !== null && hopsLeft > 0) {
            step(page.nextCursor, hopsLeft - 1);
            return;
          }
          setMoreState('idle');
        },
        () => {
          if (session.current !== startedIn) return;
          setMoreState('error');
        },
      );
    };
    step(nextCursor, MORE_HOPS_MAX - 1);
  };

  const bellName = [
    badge === null ? notificationsRu.bell : notificationsRu.bellWithNew.replace('{N}', badge),
    stale ? notificationsRu.bellStale : null,
  ]
    .filter((part) => part !== null)
    .join(', ');

  const viewerUnbound = me?.vkBinding === null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          data-stale={stale ? 'true' : 'false'}
          aria-label={bellName}
          className="relative grid h-11 w-11 place-items-center rounded-full border border-line bg-surface text-ink hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:h-9 md:w-9"
        >
          <Bell aria-hidden className="h-4 w-4" />
          {badge === null ? null : (
            // Плашка растёт ОТ значка наружу (левый край — правее центра): «20+»
            // шире «1» втрое и при привязке к правому краю наезжала на значок.
            <span
              data-testid="notification-bell-count"
              aria-hidden
              className="absolute -top-1 left-1/2 ml-2 grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-accent-foreground"
            >
              {badge}
            </span>
          )}
          {stale ? (
            <span
              data-testid="notification-bell-stale"
              aria-hidden
              className="absolute -bottom-1 left-1/2 ml-2 grid h-4 w-4 place-items-center rounded-full border border-line bg-warn-wash text-[10px] font-bold leading-none text-ink"
            >
              !
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        collisionPadding={16}
        ref={panel}
        tabIndex={-1}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          panel.current?.focus();
        }}
        data-testid="notification-panel"
        aria-labelledby={titleId}
        className="w-[22rem] max-w-[calc(100vw-2rem)] p-0"
      >
        {/* Узел существует ДО изменения списка — иначе скринридер его не объявит. */}
        <p role="status" data-testid="notification-announce" className="sr-only">
          {announce}
        </p>
        <div className="border-b border-line px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <h2 id={titleId} className="text-sm font-semibold text-ink">
              {notificationsRu.title}
            </h2>
            <button
              ref={hideButton}
              type="button"
              data-testid="notification-hide-read"
              aria-disabled={!canHide}
              onClick={hideRead}
              className={cn(
                '-my-1 -mr-2 inline-flex shrink-0 items-center rounded-sm px-2 text-xs font-medium text-accent-ink hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring aria-disabled:cursor-default aria-disabled:opacity-60 aria-disabled:hover:bg-transparent md:my-0 md:py-1',
                TOUCH_TARGET,
              )}
            >
              {notificationsRu.hideRead}
            </button>
          </div>
          <p className="mt-0.5 text-[11px] leading-snug text-muted">{notificationsRu.caption}</p>
          {viewerUnbound ? (
            // На телефоне ссылка стоит СВОЕЙ строкой: зона касания 44px внутри
            // текста раздувала плашку пустотами над и под ссылкой (ui Н3).
            <div
              data-testid="notification-unbound-note"
              className="mt-1.5 rounded-sm bg-warn-wash px-2 pt-1.5 text-xs text-ink md:pb-1.5"
            >
              <p className="flex items-start gap-1.5">
                <TriangleAlert aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {notificationsRu.unbound}{' '}
                  <Link
                    to="/profile?section=notifications"
                    onClick={() => handleOpenChange(false)}
                    className="hidden font-medium underline underline-offset-2 md:inline"
                  >
                    {notificationsRu.unboundLink}
                  </Link>
                </span>
              </p>
              <Link
                to="/profile?section=notifications"
                onClick={() => handleOpenChange(false)}
                className="-mt-2.5 flex min-h-11 items-center pl-5 font-medium underline underline-offset-2 md:hidden"
              >
                {notificationsRu.unboundLink}
              </Link>
            </div>
          ) : null}
        </div>
        <div className="max-h-[min(28rem,70vh)] overflow-y-auto p-1">
          {head.isPending ? (
            <LoadingRows />
          ) : (
            <>
              {head.isError ? (
                <div role="alert" className="px-2 py-2 text-xs text-ink">
                  {notificationsRu.error}{' '}
                  <button
                    type="button"
                    data-testid="notification-retry"
                    className={cn(
                      'inline-flex items-center font-medium underline underline-offset-2',
                      TOUCH_TARGET,
                    )}
                    onClick={() => void head.refetch()}
                  >
                    {notificationsRu.retry}
                  </button>
                </div>
              ) : null}
              {items.length === 0 && nextCursor === null && !head.isError ? (
                <p data-testid="notification-empty" className="px-2 py-3 text-xs text-muted">
                  {notificationsRu.empty}
                </p>
              ) : null}
              {visible.length > 0 ? (
                <ul className="divide-y divide-line" aria-busy={moreState === 'loading'}>
                  {visible.map((item) => (
                    <NotificationRow
                      key={item.id}
                      item={item}
                      unseen={isNew(item)}
                      level={level}
                      onNavigate={() => handleOpenChange(false)}
                    />
                  ))}
                </ul>
              ) : null}
              {moreState === 'error' ? (
                <p role="alert" className="px-2 pt-2 text-xs text-ink">
                  {notificationsRu.moreError}
                </p>
              ) : null}
              {nextCursor !== null ? (
                <button
                  type="button"
                  data-testid="notification-more"
                  aria-disabled={moreState === 'loading'}
                  onClick={loadMore}
                  className={cn(
                    'mt-1 w-full rounded-sm px-2 py-1.5 text-xs font-medium text-accent-ink hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring aria-disabled:opacity-60',
                    TOUCH_TARGET,
                  )}
                >
                  {moreState === 'loading'
                    ? notificationsRu.moreLoading
                    : moreState === 'error'
                      ? notificationsRu.retry
                      : notificationsRu.more}
                </button>
              ) : visible.length > 0 && hiddenCount === 0 ? (
                <p
                  data-testid="notification-end"
                  className="px-2 py-2 text-center text-[11px] text-muted"
                >
                  {notificationsRu.end}
                </p>
              ) : null}
              {hiddenCount > 0 ? (
                // «За 30 дней уведомлений нет» и «это все уведомления» здесь
                // были бы неправдой: строки есть, они скрыты. На телефоне
                // ссылка стоит своей строкой — как в плашке «бот не привязан».
                <div
                  data-testid="notification-hidden-note"
                  data-empty={visible.length === 0 ? 'true' : 'false'}
                  className="px-2 pt-2 text-xs text-muted md:pb-2"
                >
                  <span>
                    {visible.length === 0 && nextCursor === null && !head.isError
                      ? notificationsRu.hiddenEmpty
                      : notificationsRu.hiddenNote}
                  </span>{' '}
                  <button
                    type="button"
                    data-testid="notification-show-hidden"
                    className="-mt-2 flex min-h-11 items-center font-medium text-ink underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring md:mt-0 md:inline md:min-h-0"
                    onClick={showHidden}
                  >
                    {notificationsRu.showHidden}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Граница ошибок колокольчика: бросок внутри него не гасит экран.
 *
 * Колокольчик стоит в оболочке каждого экрана. Без границы любое исключение в
 * его отрисовке (неожиданная форма данных, ошибка в форматировании строки)
 * гасило бы экран целиком — человек терял бы работу из-за вспомогательного
 * значка.
 *
 * ⚠️ НА МЕСТЕ СЛОМАННОГО КОЛОКОЛЬЧИКА — НЕАКТИВНЫЙ ЗНАЧОК С ПОДСКАЗКОЙ, а не
 * пустота. Молча исчезнувший колокольчик неотличим от «уведомлений нет», а для
 * человека без бота это единственный канал (приёмка ui, Н2).
 *
 * ⚠️ СМЕНА МАРШРУТА СБРАСЫВАЕТ ГРАНИЦУ (`resetKey`): сбой бывает разовым, и
 * держать значок мёртвым до перезагрузки страницы незачем. Исправного
 * колокольчика смена маршрута не перемонтирует — ключ сравнивается только
 * тогда, когда граница уже сработала.
 */
class NotificationBellBoundary extends React.Component<
  { children: React.ReactNode; resetKey: string },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error): void {
    console.error('NotificationBell failed to render', error);
  }

  override componentDidUpdate(previous: { resetKey: string }): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  override render(): React.ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <span
        data-testid="notification-bell-broken"
        role="img"
        aria-label={notificationsRu.bellBroken}
        title={notificationsRu.bellBroken}
        className="grid h-11 w-11 place-items-center rounded-full border border-line bg-surface-2 text-muted md:h-9 md:w-9"
      >
        <BellOff aria-hidden className="h-4 w-4" />
      </span>
    );
  }
}

export function NotificationBell(): React.ReactElement {
  const { pathname } = useLocation();
  return (
    <NotificationBellBoundary resetKey={pathname}>
      <NotificationBellInner />
    </NotificationBellBoundary>
  );
}
